import useSWR from 'swr';
import axiosInstance from 'utils/axiosInstance';
import { useErrorContext } from 'context/hooks/useErrorContext';
import { usePageContext } from 'context/hooks/usePageContext';
import { PAGE_SUB, PAGES } from 'context/pageContext';
import { useCallback, useEffect } from 'react';
import { useTransactionContext } from 'context/hooks/useTransactionContext';
import { PAYMENT_PROVIDER } from 'types/enums';
import { getResponseMessage } from 'utils';

type UseGetCardPaymentStatusOptions = {
  retry?: boolean;
  interval?: number;
  shouldFetch?: boolean;
  // don't act on a failed status yet, e.g. while the cardholder is still on
  // the bank's page. A function decides per status; keep it stable (useCallback)
  // or the status is acted on again on every render.
  holdFailure?: boolean | ((payment: CardPaymentStatus) => boolean);
};

export const CARD_PAYMENT_SUCCESS_EVENT = 'card-payment-success';

const normalizeStatus = (value: unknown) => {
  if (typeof value !== 'string') return undefined;

  const normalized = value.trim().toUpperCase();
  return normalized || undefined;
};

export type CardPaymentStatus = {
  status?: string;
  // why the payment ended as it did, e.g. "Transaction declined. Please use
  // the test card."
  message?: string;
};

const readStatus = (res: { data?: any }) =>
  normalizeStatus(res.data?.status) ||
  normalizeStatus(res.data?.transactionStatus) ||
  normalizeStatus(res.data?.response) ||
  normalizeStatus(res.data?.data?.paymentAttempts?.[0]?.transactionStatus) ||
  normalizeStatus(res.data?.data?.transactionStatus) ||
  res.data?.transactionStatus ||
  res.data?.response ||
  res?.data?.status;

const fetcher = (url: string): Promise<CardPaymentStatus> =>
  axiosInstance.get(url).then((res) => ({
    status: readStatus(res),
    message: getResponseMessage(res.data),
  }));

const useGetCardPaymentStatus = (
  paymentId: string,
  options: UseGetCardPaymentStatusOptions = {},
  url = '/payments/status',
) => {
  const { setError: updateError } = useErrorContext();
  const { page, changePage, changeSub } = usePageContext();
  const { ceviantTransactionId, provider } = useTransactionContext();

  const getStatusUrl = () => {
    switch (provider) {
      case PAYMENT_PROVIDER.PAYLOFT:
      case PAYMENT_PROVIDER.FIRSTCHEKOUT:
        return `${url}/${paymentId}`;
      default:
        return `${url}/${ceviantTransactionId || paymentId}`;
    }
  };

  const swrKey = options.shouldFetch && paymentId ? getStatusUrl() : null;

  const {
    data,
    error,
    isLoading,
    mutate,
  } = useSWR(swrKey, fetcher, {
    refreshInterval: options.retry ? options.interval || 5000 : 0,
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  const status = data?.status;
  const statusMessage = data?.message;
  const holdFailure = options.holdFailure;

  useEffect(() => {
    if (!status) return;

    if (status === 'APPROVED' || status === 'SUCCESS') {
      window.dispatchEvent(
        new CustomEvent(CARD_PAYMENT_SUCCESS_EVENT, {
          detail: { status },
        }),
      );

      if (page === PAGES.CARD) {
        changePage(PAGES.AUTHENTICATING);
        changeSub(PAGE_SUB.SUCCESS);
      }
    } else if (
      !(typeof holdFailure === 'function'
        ? holdFailure({ status, message: statusMessage })
        : holdFailure) &&
      status !== 'INITIALISED' &&
      status !== 'PENDING' &&
      status !== 'PROCESSING' &&
      status !== 'IN_PROGRESS' &&
      status !== 'INITIATED'
    ) {
      updateError({ status, message: statusMessage });
      changeSub(PAGE_SUB.ERROR, statusMessage || status);
    }
  }, [
    status,
    statusMessage,
    holdFailure,
    page,
    changePage,
    changeSub,
    updateError,
  ]);

  const triggerFetch = useCallback(() => mutate(), [mutate]);

  return {
    status,
    message: statusMessage,
    loading: isLoading,
    error,
    triggerFetch,
  };
};

export default useGetCardPaymentStatus;
